import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import DepartmentImage from '../../../assets/image/department/firefighter-training-readiness-showcasing-firefighters-rigorous-training-scenarios-highlighting-their-commitment-accountability-public-safety_765582-10575.jpg';
import DepartmentImage2 from '../../../assets/image/department/emergency-evacuation-plan_93675-51383.jpg';

function EmergencyManagement() {
  const [Faq, setFaq] = useState(0);

  // pick up theme colors from existing DOM
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');
  const [bodyColor, setBodyColor] = useState('');

  useEffect(() => {
    if (titleRef.current) setTitleColor(window.getComputedStyle(titleRef.current).color);
    if (bodyRef.current) setBodyColor(window.getComputedStyle(bodyRef.current).color);
  }, []);

  const overviewEmphasis = { color: titleColor || '#0f172a' };

  // compact stacked subtitle/description layout to avoid awkward wraps
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35, color: titleColor || '#0f172a' };
  const valText = { fontSize: '0.95rem', lineHeight: 1.6, color: bodyColor || undefined };

  const Drop = [
    {
      Que: 'How can I prepare for natural disasters?',
      Answer:
        'Use our household emergency plan template, register for alerts, build a 72-hour kit, and know your evacuation zones/routes. Attend community drills and review checklists for power outages, severe weather, wildfire smoke, and flooding.'
    },
    {
      Que: 'What is the role of this department during emergencies?',
      Answer:
        'We activate the Emergency Operations Center, coordinate agencies and mutual aid, manage incident communications, allocate resources, support sheltering/mass care, and transition into damage assessment and recovery operations.'
    },
    {
      Que: 'How can I access emergency services?',
      Answer:
        'Call 911 for life-threatening emergencies. For non-emergency assistance during incidents, follow instructions in public alerts and check the Incident Information page for shelters, road closures, and assistance locations.'
    }
  ];

  return (
    <>
      <section className="department-details-section">
        <div className="container">
          <div className="row">

            {/* Sidebar */}
            <div className="col-12 col-lg-4 col-xl-4">
              <Sidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-8">
              <div className="department-details-imgbox">
                <img src={DepartmentImage} className="img-fluid" alt="Emergency Management" />
              </div>

              <div className="department-details-content-box">
                <h4 ref={titleRef} className="department-details-title">Emergency Management</h4>
                <p style={overviewEmphasis}>
                  We protect lives and property through preparedness, coordinated response, timely public information,
                  and resilient recovery. Priorities: readiness, rapid incident coordination, and risk reduction.
                </p>
                <p>
                  We plan with local responders, health, public works, schools, and nonprofits; run exercises; maintain
                  hazard plans; and manage operations during incidents—from severe weather and wildfires to hazardous
                  materials, public health events, and infrastructure outages.
                </p>
              </div>

              <div className="department-details-box">
                <div className="department-details-policy">
                  <span>
                    Ensuring readiness and rapid response to any crisis.
                  </span>
                </div>
                <p ref={bodyRef}>
                  We deliver training and tabletop drills, stage supplies, manage alerting systems, and coordinate sheltering
                  and recovery services. Our team supports vulnerable residents, disability access and functional needs,
                  and multilingual communications so help reaches everyone.
                </p>
              </div>

              <div className="department-details-benefits-inner-box">
                <div className="row row-gutter-30">
                  <div className="col-lg-6">
                    <div className="department-details-benefits-box-image">
                      <img src={DepartmentImage2} className="img-fluid" alt="Emergency Response" />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="department-details-benefits-box">
                      <h3>Our Benefits</h3>
                      <p>
                        Safer neighborhoods, faster response, and quicker recovery after disasters.
                      </p>
                      <ul className="list-unstyled list-style">
                        <li>
                          <Link to="/department/emergency/disaster-preparedness">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Disaster Preparedness and Training
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/emergency/emergency-response">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Emergency Response and Recovery Operations
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/emergency/public-alerts">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Public Alerts and Warning Systems
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/emergency/community-resilience">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Community Resilience and Recovery Programs
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Outcomes section */}
              <div className="department-details-content-box" style={{ marginTop: '10px' }}>
                <h4
                  className="department-details-title"
                  style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}
                >
                  Outcomes we aim for
                </h4>
                <p style={{ color: bodyColor || undefined, fontSize: '0.95rem' }}>
                  Faster warnings, shorter response times, fewer injuries and displacements, reduced damage through
                  mitigation, and a quicker return to normal with equitable access to assistance and services.
                </p>
              </div>

              {/* What We Do — compact stacked layout */}
              <div className="department-details-benefits-box">
                <h3>What We Do</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Preparedness & Training</div>
                      <div style={valText}>
                        Hazard plans, evacuation maps, public workshops, CERT training, school drills, and continuity
                        planning for essential services.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Response & Incident Management</div>
                      <div style={valText}>
                        Emergency Operations Center activation, mutual aid coordination, situational awareness,
                        resource requests, shelter/mass care, and damage assessment.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Recovery & Individual Assistance</div>
                      <div style={valText}>
                        Debris management, temporary housing coordination, disaster case management, small business support,
                        and long-term reconstruction planning.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Mitigation & Risk Reduction</div>
                      <div style={valText}>
                        Grant funding for hardening infrastructure, flood and fire risk projects, safe-room initiatives,
                        and code updates to reduce future losses.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Alerts & Public Information</div>
                      <div style={valText}>
                        Multi-channel warnings (SMS, email, sirens), social updates, accessible formats, and
                        multilingual messaging for real-time protective actions.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Critical Infrastructure & Continuity</div>
                      <div style={valText}>
                        Coordination with utilities and hospitals, redundant communications, and continuity of operations
                        (COOP/COG) to keep government services running.
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="accordian-box">
                {Drop.map((items, index) => (
                  <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                    <div className="accordian-title">
                      <h5>{items.Que}</h5>
                      <i className="fa-solid fa-angle-right" onClick={() => setFaq(index)} />
                    </div>

                    {Faq === index && (
                      <div className="accordian-content" style={{ display: 'block' }}>
                        <p>{items.Answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default EmergencyManagement;
